import React from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { ExternalLink, ShieldAlert } from 'lucide-react';
import { createPortal } from 'react-dom';
import { AppSelectDropdown } from './AppSelectDropdown';
import { GlobalSearch } from './GlobalSearch';
import { SETTINGS_PATH_PERSONAL } from '../utils/settingsRoutes';
import { supabase } from '../lib/supabaseClient';
import { IMPRESSUM_URL } from '../lib/legalLinks';
import { resetPostHogAfterLogout } from '../lib/posthogClient';
import { resetSentryAfterLogout } from '../lib/sentryClient';
import { clearSidebarPinnedSnapshot } from '../lib/sidebarPinnedPreference';
import { fetchMe, invalidateMeCache } from '../lib/meClient';
import { canOpenAppPath, normalizeModuleMap } from '../lib/instanceModules';
import { dashboardPath } from '../utils/dashboardRoutes';
import { AdminPanel } from './AdminPanel';
import { NotificationsBell } from './NotificationsBell';

interface AppTopBarProps {
  /** Leer lassen, wenn der Titel z. B. in einer inneren Seitenleiste steht */
  title?: string;
  /** z. B. Stammdaten-Umschalter links neben dem Titel */
  leftAccessory?: React.ReactNode;
}

export function AppTopBar({ title = '', leftAccessory }: AppTopBarProps) {
  const navigate = useNavigate();
  const location = useLocation();
  const [instances, setInstances] = React.useState<Array<{ id: string; name: string }>>([]);
  const [activeInstanceId, setActiveInstanceId] = React.useState<string>('default');
  const [displayName, setDisplayName] = React.useState<string>('D');
  const [currentUserId, setCurrentUserId] = React.useState<string | null>(null);
  const [companyName, setCompanyName] = React.useState<string | null>(null);
  const [isSuperAdmin, setIsSuperAdmin] = React.useState(false);
  const [adminPanelOpen, setAdminPanelOpen] = React.useState(false);
  const [userMenuOpen, setUserMenuOpen] = React.useState(false);
  const userMenuRef = React.useRef<HTMLDivElement | null>(null);
  const userMenuPortalRef = React.useRef<HTMLDivElement | null>(null);
  const [userMenuFixed, setUserMenuFixed] = React.useState<{ top: number; right: number } | null>(null);

  const replaceInstanceInPath = React.useCallback(
    (instanceId: string) => {
      const path = location.pathname;
      if (/^\/i\/[^/]+/.test(path)) return path.replace(/^\/i\/[^/]+/, `/i/${encodeURIComponent(instanceId)}`);
      return `/i/${encodeURIComponent(instanceId)}/dashboard`;
    },
    [location.pathname]
  );

  React.useEffect(() => {
    let cancelled = false;
    fetchMe()
      .then((data) => {
        if (cancelled || !data) return;
        const list = Array.isArray(data.instances) ? data.instances : [];
        setInstances(list.map((i) => ({ id: i.id, name: String(i.name ?? '') })));
        const active = data.activeInstance?.id || list[0]?.id || 'default';
        setActiveInstanceId(active);
        try {
          localStorage.setItem('activeInstanceId', active);
        } catch {
          // noop
        }
        const rawName = String(data.user?.username || data.user?.email || '').trim();
        setDisplayName(rawName);
        setCurrentUserId(data.user?.id ?? null);
        setCompanyName(data.companyName ?? null);
        setIsSuperAdmin(data.is_super_admin === true);
      })
      .catch(() => {
        // noop
      });
    return () => {
      cancelled = true;
    };
  }, []);

  const initials = React.useMemo(() => {
    const parts = displayName
      .trim()
      .split(/\s+/)
      .filter(Boolean);
    if (parts.length >= 2) return `${parts[0][0] ?? ''}${parts[1][0] ?? ''}`.toUpperCase();
    return (parts[0]?.slice(0, 2) || '?').toUpperCase();
  }, [displayName]);

  const handleSwitchInstance = React.useCallback(
    async (nextId: string) => {
      if (!nextId || nextId === activeInstanceId) return;
      try {
        const res = await fetch('/api/me/active-instance', {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ instanceId: nextId }),
        });
        if (!res.ok) throw new Error('Instanzwechsel fehlgeschlagen');
        invalidateMeCache();
        setActiveInstanceId(nextId);
        localStorage.setItem('activeInstanceId', nextId);
        // Gleiche Seite in der neuen Instanz, außer deren Modul ist dort abgewählt oder nicht erlaubt.
        const target = replaceInstanceInPath(nextId);
        const me = await fetchMe();
        const allowed = !me || canOpenAppPath(target, normalizeModuleMap(me.modules), me.capabilities ?? {});
        navigate(allowed ? target : dashboardPath(nextId), { replace: true });
      } catch (error) {
        console.error('Instance switch failed:', error);
      }
    },
    [activeInstanceId, navigate, replaceInstanceInPath],
  );

  React.useLayoutEffect(() => {
    if (!userMenuOpen) {
      setUserMenuFixed(null);
      return;
    }
    const updatePos = () => {
      const btn = userMenuRef.current?.querySelector('button');
      if (!btn) return;
      const r = btn.getBoundingClientRect();
      setUserMenuFixed({ top: r.bottom + 8, right: window.innerWidth - r.right });
    };
    updatePos();
    window.addEventListener('resize', updatePos);
    document.addEventListener('scroll', updatePos, true);
    return () => {
      window.removeEventListener('resize', updatePos);
      document.removeEventListener('scroll', updatePos, true);
    };
  }, [userMenuOpen]);

  React.useEffect(() => {
    if (!userMenuOpen) return;
    const onOutside = (e: MouseEvent) => {
      const t = e.target as Node;
      if (userMenuRef.current?.contains(t)) return;
      if (userMenuPortalRef.current?.contains(t)) return;
      setUserMenuOpen(false);
    };
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') setUserMenuOpen(false);
    };
    document.addEventListener('mousedown', onOutside);
    document.addEventListener('keydown', onKey);
    return () => {
      document.removeEventListener('mousedown', onOutside);
      document.removeEventListener('keydown', onKey);
    };
  }, [userMenuOpen]);

  return (
    <>
    {/* Spacer for the fixed mobile top bar — keeps content below the nav bar */}
    <div className="h-14 md:hidden shrink-0" aria-hidden />
    <header className="relative z-20 hidden h-14 items-center bg-app-surface px-4 md:flex border-b border-app-border">
      <div className="relative z-10 flex min-w-0 flex-1 items-center gap-3">
        {leftAccessory}
        {title ? <h1 className="truncate text-lg font-bold text-app-text">{title}</h1> : null}
      </div>

      <GlobalSearch instanceId={activeInstanceId} />

      <div className="relative z-10 flex flex-1 items-center justify-end gap-3">
        {instances.length > 0 ? (
          <AppSelectDropdown
            value={activeInstanceId}
            onChange={(v) => {
              void handleSwitchInstance(v);
            }}
            options={instances.map((instance) => ({
              value: instance.id,
              label: (instance.id === activeInstanceId && companyName) ? companyName : instance.name,
            }))}
            placeholder="Instanz wechseln"
            className="w-52 max-w-[13rem]"
            triggerClassName="!border-0 !bg-transparent !shadow-none !px-2 !py-1 !font-normal !text-app-text-subtle hover:!bg-app-surface-muted/80 hover:!text-app-text-muted hover:!border-transparent focus:!ring-0 focus:!border-transparent rounded-md"
            chevronClassName="!h-3.5 !w-3.5 !text-app-text-subtle"
          />
        ) : null}
        <NotificationsBell userId={currentUserId} instanceId={activeInstanceId} />
        <div className="relative ml-2" ref={userMenuRef}>
          <button
            type="button"
            onClick={() => setUserMenuOpen((v) => !v)}
            className="w-8 h-8 rounded-full bg-pipedrive-blue flex items-center justify-center text-white font-bold text-xs"
            title="Benutzermenü"
          >
            {initials}
          </button>
        </div>
      </div>

      {userMenuOpen &&
        userMenuFixed &&
        createPortal(
          <div
            ref={userMenuPortalRef}
            className="fixed w-52 rounded-lg border border-app-border bg-app-surface shadow-xl py-1 z-[10000]"
            style={{ top: userMenuFixed.top, right: userMenuFixed.right }}
          >
            <button
              type="button"
              onClick={() => {
                setUserMenuOpen(false);
                const targetPath = /^\/i\/[^/]+/.test(location.pathname)
                  ? location.pathname.replace(/^\/i\/[^/]+/, `/i/${encodeURIComponent(activeInstanceId)}`)
                  : `/i/${encodeURIComponent(activeInstanceId)}${SETTINGS_PATH_PERSONAL}`;
                navigate(
                  targetPath.includes('/settings/')
                    ? targetPath.replace(/\/settings\/[^/?#]*/, SETTINGS_PATH_PERSONAL)
                    : `/i/${encodeURIComponent(activeInstanceId)}${SETTINGS_PATH_PERSONAL}`
                );
              }}
              className="w-full text-left px-3 py-2 text-sm text-app-text-muted hover:bg-app-hover"
            >
              Persönliche Einstellungen
            </button>
            {isSuperAdmin && (
              <>
                <div className="border-t border-app-border my-1" />
                <button
                  type="button"
                  onClick={() => { setUserMenuOpen(false); setAdminPanelOpen(true); }}
                  className="w-full text-left px-3 py-2 text-sm text-violet-700 hover:bg-violet-50 font-medium flex items-center gap-2"
                >
                  <ShieldAlert className="w-4 h-4 shrink-0" />
                  Super Admin
                </button>
              </>
            )}
            <div className="border-t border-app-border my-1" />
            <a
              href={IMPRESSUM_URL}
              target="_blank"
              rel="noopener noreferrer"
              onClick={() => setUserMenuOpen(false)}
              className="w-full px-3 py-2 text-sm text-app-text-muted hover:bg-app-hover flex items-center justify-between gap-2"
            >
              Impressum
              <ExternalLink className="w-3.5 h-3.5 shrink-0" />
            </a>
            <div className="border-t border-app-border my-1" />
            <button
              type="button"
              onClick={async () => {
                setUserMenuOpen(false);
                try {
                  await Promise.all([
                    fetch('/api/auth/logout', { method: 'POST' }),
                    supabase.auth.signOut(),
                  ]);
                } catch {
                  // noop
                }
                clearSidebarPinnedSnapshot();
                invalidateMeCache();
                resetPostHogAfterLogout();
                resetSentryAfterLogout();
                navigate('/login', { replace: true });
              }}
              className="w-full text-left px-3 py-2 text-sm text-app-text-muted hover:bg-app-hover"
            >
              Abmelden
            </button>
          </div>,
          document.body
        )}

      {adminPanelOpen && <AdminPanel onClose={() => setAdminPanelOpen(false)} />}
    </header>
    </>
  );
}
